import { HomeQuery } from './../../services/home/home.query';
import { BillService } from './../../services/bill/bill.service';
import { BillQuery } from '@1bill-app/services/bill/bill.query';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BillCategoryKey, GetBillOption } from '@1bill-app/services/bill/types';
import { SubSink } from 'subsink';
import { NGXLogger } from 'ngx-logger';
import { HomeService } from '@1bill-app/services/home/home.service';
import {
  EXPLANATION_NOTES_SIMPLE,
  MOBILE_VIEW_THRESHOLD_PX,
  ROUTE_BILLS_PAGE,
  ROUTE_HOMEPAGE,
} from '@1bill-app/constants';
import { trigger, style, transition, animate } from '@angular/animations';
import { IonSearchbar, Platform, PopoverController } from '@ionic/angular';
import { StatusQuery } from '@1bill-app/services/status/status.query';
import { catchError, map } from 'rxjs/operators';
import { PopoverSimpleComponent } from '@1bill-app/components/popover/popover-simple/popover-simple.component';
import { Router } from '@angular/router';

/**
 * Page for showing all the bills.
 *
 * URL: /bills
 */
@Component({
  selector: 'app-bills',
  templateUrl: './bills.page.html',
  styleUrls: ['./bills.page.scss'],
  animations: [
    trigger('fadeAnimation', [
      transition(':increment', [style({ opacity: 0 }), animate(600)]),
      transition(':decrement', [style({ opacity: 0 }), animate(600)]),
    ]),
  ],
})
export class BillsPage implements OnInit, OnDestroy {
  @ViewChild(IonSearchbar) searchBar: IonSearchbar;
  subs = new SubSink();
  public billCategories$ = this.billQuery.billCategories$.pipe(
    map((categories) => categories?.filter((x) => x.key !== 'ALL')),
  );
  public combinedDueAmount$ = this.billQuery.combinedDueAmount$;
  public calculatedDueAmount$ = this.billQuery.calculatedDueAmount$;
  public isBillLoading$ = this.billQuery.isLoading$;

  // List shows unpaid bills by default, until billStatusFilter is changed.
  public billsDisplayList$ = this.billQuery.getBillsPageUpcomingBills(
    GetBillOption.UNPAID_PLUS_FAILED_BILLS,
  );
  public filter$ = this.billQuery.select((s) => s?.ui?.billsPageFilter);
  // public billTags$ = this.billQuery.getBillTags(4);
  public billTags$ = this.billQuery.getBillTagsForPage(GetBillOption.ALL_BILLS);
  public loading$ = this.homeQuery.isLoading$;
  public isPullToRefresh: boolean;
  public jobsOnProgress$ = this.statusQuery.jobsOnProgress$;
  isTechnicalIssue: boolean = false;
  routeBillsPage = ROUTE_BILLS_PAGE;
  billStatusFilter: 'all' | 'calendar' | 'paid' | 'unpaid' = 'unpaid';
  showFilters: boolean = false;
  defaultHref = ROUTE_HOMEPAGE;
  get isDesktopView() {
    return this.platform.width() >= MOBILE_VIEW_THRESHOLD_PX;
  }

  constructor(
    private homeQuery: HomeQuery,
    private billQuery: BillQuery,
    private billService: BillService,
    private logger: NGXLogger,
    private homeService: HomeService,
    private statusQuery: StatusQuery,
    private popoverController: PopoverController,
    private platform: Platform,
    private router: Router,
  ) {}

  searchSubmit() {
    this.searchBar.getInputElement().then((el) => el.blur());
  }

  onSearchChange(value: string) {
    this.logger.log('Searching for: ' + value);
    this.billService.updateBillsPageFilter({ query: value });
  }

  ngOnInit() {
    this.billService
      .getBill()
      .pipe(
        catchError((err) => {
          if (err?.ok === false) this.isTechnicalIssue = true;
          throw err;
        }),
      )
      .subscribe();

    this.subs.sink = this.homeService.homeDataEvent$.subscribe((trigger) => {
      if (trigger) {
        this.logger.debug('BillsPage::homeDataEvent$', trigger);
        this.subscribeAllData();
      }
    });

    // this.router.routerState.root.fragment.subscribe((frag) => {
    //   // Check if the fragment is a valid selector for the ion-segment
    //   if (frag === 'unpaid' || frag === 'paid') {
    //     this.billStatusFilter = frag;
    //     this.billStatusFilterChanged(frag);
    //   }
    // });
  }

  subscribeAllData() {
    this.subs.sink = this.homeService.fetchAllData().subscribe();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onCatBtnClick(categoryKey: BillCategoryKey) {
    this.billService.updateBillsPageFilter({ categoryKeys: [categoryKey] });
  }

  onPropertyTagClick(tag: string) {
    this.billService.updateBillsPageFilter({ propertyTags: [tag] });
  }

  billStatusFilterChanged(status: 'paid' | 'unpaid' | 'all' | 'calendar') {
    //this.billsDisplayList$ = null;
    switch (status) {
      case 'unpaid': {
        this.billsDisplayList$ = this.billQuery.getBillsPageUpcomingBills(
          GetBillOption.UNPAID_PLUS_FAILED_BILLS,
        );
        break;
      }

      case 'paid': {
        this.billsDisplayList$ = this.billQuery.getBillsPageUpcomingBills(
          GetBillOption.PAID_BILLS,
        );
        break;
      }

      case 'all': {
        this.billsDisplayList$ = this.billQuery.getBillsPageUpcomingBills(
          GetBillOption.ALL_BILLS,
        );
        break;
      }
    }

    //this.billService.getBill(true).subscribe();
  }

  doRefresh(event) {
    this.isPullToRefresh = true;
    this.billService.getBill(true).subscribe({
      next: () => {
        this.isPullToRefresh = false;
        event.target.complete();
      },
      error: () => {
        this.isPullToRefresh = false;
        event.target.complete();
      },
    });
  }

  async presentInfoPopoverFilter() {
    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-popover-simple',
      // translucent: true,
      mode: 'ios',
      componentProps: {
        popoverTitle: EXPLANATION_NOTES_SIMPLE.FILTER_BILL_TITLE,
        description: EXPLANATION_NOTES_SIMPLE.FILTER_BILL_DESC,
      },
    });
    return await popover.present();
  }
}
